// formHelpers.ts

import { ICustomFieldData, IFormFields, IMergedSchemaField } from "./graphql/interface"

export const prepareFormData = async (
    formData: Record<string, any>,
    schema: { fields: Array<IFormFields> },
    customFieldData: ICustomFieldData
): Promise<Array<IMergedSchemaField>> => {
    let lastLabel: string | null = null

    // Pre-create a map of schema fields for quick lookup
    const schemaFieldMap = schema?.fields?.reduce(
        (acc, field) => {
            acc[field.name] = field
            return acc
        },
        {} as Record<string, IFormFields>
    )

    const mergedSchemaFields = Object.keys(formData)
        .map((key) => {
            const schemaField = schemaFieldMap[key]
            if (!schemaField) return null

            // If the field is a label, set it as the lastLabel and skip further processing
            if (schemaField?.meta?.displayType === "label") {
                lastLabel = key
                return null
            }

            // Default to field name if displayName is not defined
            let displayName = schemaField?.meta?.displayName || key

            // Replace displayName with the label's displayName if the conditions match
            if (lastLabel && ["text", "file"].includes(schemaField?.meta?.displayType)) {
                const labelField = schemaFieldMap[lastLabel]
                if (labelField) {
                    displayName = labelField?.meta?.displayName || lastLabel
                    lastLabel = null // Reset lastLabel after use
                }
            }

            // Always include fields, even with null, undefined, or empty values
            const options = schemaField?.meta?.options || {}
            const formValue = formData[key] // Get the current value from formData

            // Find the index of the action inside options (if it's an array)
            const actionIndex = Array.isArray(options)
                ? options.findIndex((opt) => opt.value === formValue)
                : Object.keys(options).indexOf(String(formValue)) // If it's an object, find index by key

            const severity = schemaField?.meta?.options?.[actionIndex]?.action || "none"

            return {
                name: key,
                displayName,
                value: formData[key] ?? null, // Ensure null values are included
                severity,
            }
        })
        .filter((field) => field !== null)

    // Merge customFieldData
    if (Object.keys(customFieldData).length > 0) {
        Object.keys(customFieldData).forEach((customFieldKey) => {
            const customField = customFieldData[customFieldKey]

            // Find the matching field by "name"
            const existingFieldIndex = mergedSchemaFields.findIndex((field) => field.name === customFieldKey)

            // Prepare value object
            const customFieldValue = {
                text: customField.text || "",
                files: customField.files ? customField.files.map((file: File) => file) : [],
            }

            if (existingFieldIndex !== -1) {
                // Update existing field
                mergedSchemaFields[existingFieldIndex] = {
                    ...mergedSchemaFields[existingFieldIndex],
                    value: customFieldValue,
                    severity: customField?.severity || "default",
                }
            } else {
                // Add new field if not found
                mergedSchemaFields.push({
                    name: customFieldKey,
                    displayName: customField.displayName || "",
                    value: customFieldValue,
                    severity: customField?.severity || "default",
                })
            }
        })
    }

    return mergedSchemaFields
}

export const uploadFileToS3WithProgress = (uploadUrl: string, file: File, onProgress: (percentage: number) => void) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentage = Math.round((event.loaded / event.total) * 100)
                onProgress(percentage)
            }
        }
        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve(xhr.response)
            } else {
                reject(new Error(`Upload failed with status: ${xhr.status}`))
            }
        }
        xhr.onerror = () => {
            reject(new Error("Upload failed due to a network error"))
        }
        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
    })
}

export const collectFilesFromForm = (formData: Record<string, any>) => {
    const filesToUpload: File[] = []
    // Iterate through formData fields and collect files
    Object.keys(formData).forEach((key) => {
        const field = formData[key]
        if (field && field.files && Array.isArray(field.files)) {
            filesToUpload.push(...field.files)
        }
    })
    return filesToUpload
}
